import { useContext, useState } from 'react';
import { SiteContentContext } from '../context/SiteContentContext';

const MONTHS_FULL = ['Январь','Февраль','Март','Апрель','Май','Июнь',
  'Июль','Август','Сентябрь','Октябрь','Ноябрь','Декабрь'];
const DAYS_SHORT = ['Пн','Вт','Ср','Чт','Пт','Сб','Вс'];

export const TYPE_COLOR = { study: '#4caf50', adventures: '#2196f3', races: '#db782e', vacation: '#9c27b0' };
export const TYPE_LABEL = { study: 'Обучение', adventures: 'Путешествия', races: 'Регаты', vacation: 'Отпуск' };

function localIso(d) {
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

export function expandRange(startIso, endIso, durationDays) {
  const start = new Date(startIso + 'T00:00:00');
  let end;
  if (endIso) {
    end = new Date(endIso + 'T00:00:00');
  } else if (durationDays && durationDays > 1) {
    end = new Date(start);
    end.setDate(end.getDate() + Number(durationDays) - 1);
  } else {
    return [startIso];
  }
  const dates = [];
  const cur = new Date(start);
  while (cur <= end) {
    dates.push(localIso(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

export function buildEventMap(cards, instructors = []) {
  const events = {};
  const addDay = (iso, entry) => {
    if (!events[iso]) events[iso] = [];
    events[iso].push(entry);
  };
  for (const type of ['study', 'adventures', 'races']) {
    for (const card of (cards[type] || [])) {
      if (!card.isPublished) continue;
      const color = TYPE_COLOR[type];
      if (card.availableDates?.length > 0) {
        card.availableDates.forEach(slot => {
          const iso = typeof slot === 'object' ? slot.isoDate : null;
          if (!iso) return;
          const days = expandRange(iso, null, card.durationDays);
          days.forEach((d, i) => addDay(d, {
            title: card.title, type, color,
            isStart: i === 0, isEnd: i === days.length - 1,
          }));
        });
      } else if (card.startDate) {
        const days = expandRange(card.startDate, card.endDate, card.durationDays);
        days.forEach((d, i) => addDay(d, {
          title: card.title, type, color,
          isStart: i === 0, isEnd: i === days.length - 1,
        }));
      }
    }
  }
  for (const inst of instructors) {
    for (const vac of (inst.vacations || [])) {
      if (!vac.startIso || !vac.endIso) continue;
      const days = expandRange(vac.startIso, vac.endIso, null);
      days.forEach((d, i) => addDay(d, {
        title: `Отпуск: ${inst.name}`,
        type: 'vacation', color: TYPE_COLOR.vacation,
        isStart: i === 0, isEnd: i === days.length - 1,
      }));
    }
  }
  return events;
}

const BigCalendar = ({ cards }) => {
  const { content } = useContext(SiteContentContext);
  const instructors = content.instructors || [];
  const today = new Date();
  const [cur, setCur] = useState({ y: today.getFullYear(), m: today.getMonth() });

  const events = buildEventMap(cards, instructors);

  const firstDay = new Date(cur.y, cur.m, 1);
  const lastDay  = new Date(cur.y, cur.m + 1, 0);
  const startDow = (firstDay.getDay() + 6) % 7;
  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= lastDay.getDate(); d++) cells.push(d);

  const todayStr = `${today.getFullYear()}-${String(today.getMonth()+1).padStart(2,'0')}-${String(today.getDate()).padStart(2,'0')}`;

  return (
    <div style={{ background: '#fff', borderRadius: 12, padding: 24, boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
      {/* Навигация */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <button
          onClick={() => setCur(p => { const d = new Date(p.y, p.m-1); return { y: d.getFullYear(), m: d.getMonth() }; })}
          style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, width: 40, height: 40, cursor: 'pointer', fontSize: 20 }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 18, color: '#1a2a3a' }}>{MONTHS_FULL[cur.m]} {cur.y}</span>
        <button
          onClick={() => setCur(p => { const d = new Date(p.y, p.m+1); return { y: d.getFullYear(), m: d.getMonth() }; })}
          style={{ background: 'none', border: '1px solid #e0e0e0', borderRadius: 6, width: 40, height: 40, cursor: 'pointer', fontSize: 20 }}>›</button>
      </div>

      {/* Дни недели */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
        {DAYS_SHORT.map(d => (
          <div key={d} style={{ textAlign: 'center', fontSize: 13, color: '#aaa', fontWeight: 600, padding: '6px 0' }}>{d}</div>
        ))}
      </div>

      {/* Ячейки */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
        {cells.map((day, i) => {
          if (!day) return <div key={`e${i}`} style={{ minHeight: 90 }} />;
          const iso = `${cur.y}-${String(cur.m+1).padStart(2,'0')}-${String(day).padStart(2,'0')}`;
          const dayEvents = events[iso] || [];
          const isToday = iso === todayStr;
          const dow = (new Date(iso + 'T12:00:00').getDay() + 6) % 7;
          const isWeekStart = dow === 0;
          const isWeekEnd   = dow === 6;

          return (
            <div key={iso} title={dayEvents.map(e => e.title).join('\n')} style={{
              minHeight: 90,
              background: isToday ? '#fff5ee' : '#fff',
              border: isToday ? '1px solid #db782e' : '1px solid #f0f0f0',
              borderRadius: isToday ? 4 : 0,
              display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
              <span style={{
                display: 'block', textAlign: 'center', fontSize: 13,
                padding: '5px 0 3px',
                fontWeight: isToday ? 700 : 400,
                color: isToday ? '#db782e' : '#333',
              }}>{day}</span>
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2, paddingBottom: 3 }}>
                {dayEvents.map((ev, ei) => {
                  const rl = ev.isStart || isWeekStart ? 4 : 0;
                  const rr = ev.isEnd   || isWeekEnd   ? 4 : 0;
                  return (
                    <div key={ei} style={{
                      height: 18,
                      background: ev.color,
                      marginLeft:  (ev.isStart || isWeekStart) ? 2 : 0,
                      marginRight: (ev.isEnd   || isWeekEnd)   ? 2 : 0,
                      borderRadius: `${rl}px ${rr}px ${rr}px ${rl}px`,
                      opacity: 0.9,
                      overflow: 'hidden',
                      display: 'flex', alignItems: 'center',
                    }}>
                      {(ev.isStart || isWeekStart) && (
                        <span style={{ fontSize: 10, color: '#fff', paddingLeft: 5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', lineHeight: '18px' }}>
                          {ev.title}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Легенда */}
      <div style={{ display: 'flex', gap: 20, marginTop: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
        {Object.entries(TYPE_LABEL).map(([type, label]) => (
          <div key={type} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#555' }}>
            <span style={{ width: 24, height: 10, borderRadius: 5, background: TYPE_COLOR[type], display: 'block' }} />
            {label}
          </div>
        ))}
      </div>
    </div>
  );
};

export default BigCalendar;
